import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { StaffClassService, StudentBasicInfo } from '../services/staff-class.service';
import { StaffSessionService } from '../services/staff-session.service';

declare var customToastr: any;
function safeToast(message: string, type: 'success' | 'error' | 'info' = 'info') {
  if (typeof customToastr === 'function') { customToastr(message, type); }
}

interface StudentRow {
  id: number | string;
  name: string;
  rollNo: string;
  initials: string;
  raw: StudentBasicInfo;
}

@Component({
  selector: 'app-staff-class-students',
  templateUrl: './staff-class-students.component.html',
  styleUrls: ['../shared/staff-common.css', './staff-class-students.component.css']
})
export class StaffClassStudentsComponent implements OnInit {
  classId: string | null;
  loading = true;
  loadError = false;
  readonly skeletonPlaceholders = Array.from({ length: 8 }, () => ({}));

  students: StudentRow[] = [];
  isFeesEnable = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private staffClassService: StaffClassService,
    private staffSession: StaffSessionService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.classId = this.route.snapshot.paramMap.get('id');
    this.loadStudents();
    this.loadFeeAccess();
  }

  private async loadStudents() {
    if (!this.classId) {
      this.loading = false;
      this.loadError = true;
      return;
    }
    this.loading = true;
    this.loadError = false;
    try {
      const response = await firstValueFrom(this.staffClassService.getStudentBasicInfo(this.classId));
      const list = Array.isArray(response) ? response : [];
      this.students = list
        .filter(s => s.rollNumber !== undefined && s.rollNumber !== null)
        .sort((a, b) => a.rollNumber - b.rollNumber)
        .map(s => this.mapStudent(s));
    } catch (error) {
      this.loadError = true;
      safeToast(await firstValueFrom(this.translate.get('couldntLoadTheStudentListPleaseTryAgain')), 'error');
    } finally {
      this.loading = false;
    }
  }

  private async loadFeeAccess() {
    const staffDetails = this.staffSession.getStaffDetails();
    if (!staffDetails || !this.classId) { return; }
    try {
      const response = await firstValueFrom(this.staffClassService.getStaffFeeAccess(staffDetails.staffId, this.classId));
      this.isFeesEnable = Array.isArray(response) ? !!response[0]?.isStaffHasAccessForFees : false;
    } catch {
      this.isFeesEnable = false;
    }
  }

  private mapStudent(s: StudentBasicInfo): StudentRow {
    const name = s.studentName || '';
    return {
      id: s.studentId ?? s.rollNumber,
      name,
      rollNo: String(s.rollNumber),
      initials: this.staffSession.getInitials(name),
      raw: s,
    };
  }

  goBack() {
    this.router.navigate(['/staff/class', this.classId]);
  }

  openStudent(student: StudentRow) {
    this.router.navigate(['/staff/student', student.id]);
  }
}
